import type { TableName } from '@repo/common/types/SharedLib';

// ─── CONSTANTS ───

/**
 * Table names in sync order (dependencies first)
 * This is constant since sync order doesn't change at runtime
 */
export const TABLE_NAMES_IN_SYNC_ORDER: TableName[] = [
  'workspace',
  'transcript',
  'asset',
  'task',
  'conversation',
  'message',
  'calendarEvent'
];

// ─── UTILITY FUNCTIONS ───

/**
 * Get all table names in sync order (dependencies first)
 */
export function getTableNamesInSyncOrder(): TableName[] {
  return TABLE_NAMES_IN_SYNC_ORDER;
}

/**
 * Validate table name against the known table names
 */
export function isValidTableName(tableName: string): tableName is TableName {
  return TABLE_NAMES_IN_SYNC_ORDER.includes(tableName as TableName);
}

/**
 * Get all valid table names
 */
export function getValidTableNames(): TableName[] {
  return [...TABLE_NAMES_IN_SYNC_ORDER];
}

/**
 * Check if a table has dependencies
 * @param tableName - The table to check
 * @param config - The configuration object or dependencies map
 */
export function hasDependencies(
  tableName: TableName,
  config: Record<TableName, { dependencies: TableName[] }> | Record<TableName, string[]>
): boolean {
  const tableConfig = config[tableName];
  if (!tableConfig) return false;

  // Handle both config objects and dependency arrays
  if ('dependencies' in tableConfig) {
    return tableConfig.dependencies.length > 0;
  } else {
    return tableConfig.length > 0;
  }
}

/**
 * Get dependencies for a table
 * @param tableName - The table to get dependencies for
 * @param config - The configuration object or dependencies map
 */
export function getDependencies(
  tableName: TableName,
  config: Record<TableName, { dependencies: TableName[] }> | Record<TableName, string[]>
): TableName[] {
  const tableConfig = config[tableName];
  if (!tableConfig) return [];

  // Handle both config objects and dependency arrays
  if ('dependencies' in tableConfig) {
    return tableConfig.dependencies;
  } else {
    return tableConfig as TableName[];
  }
}
